import { useState } from "react"
import type { FormEvent } from "react"
import api from "../../lib/api"
import { toastError } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { CheckCircleIcon } from "../../icons"

type Student = {
  id: string
  name: string
  email: string
  createdAt: string
}

type CertificateVerification = {
  student_name: string
  course_title: string
  issued_at: string
  code: string
  is_valid: boolean
}

export default function CertificatesManager() {
  const [search, setSearch] = useState("")
  const [isVerifyOpen, setIsVerifyOpen] = useState(false)
  const [verifyCode, setVerifyCode] = useState("")
  const [verifyResult, setVerifyResult] = useState<CertificateVerification | null>(null)
  const [verifying, setVerifying] = useState(false)  const {
    rows: students,
    total: studentsTotal,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error,
    reload,
  } = useServerTable<Student>({
    endpoint: "/admin/users",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    params: { role: "student", search },
    extractRows: (data) => data.users ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  function onOpenVerify() {
    setVerifyCode("")
    setVerifyResult(null)
    setIsVerifyOpen(true)
  }

  const handleVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setVerifyResult(null)
    setVerifying(true)
    try {
      const res = await api.get(`/academy/certificates/verify/${verifyCode.trim()}`)
      setVerifyResult(res.data.data)
    } catch (err) {
      toastError(err)
    } finally {
      setVerifying(false)
    }
  }

  const columns: DataTableColumn<Student>[] = [
    {
      key: "name",
      label: "Alumno",
      sortable: true,
      render: (student) => <span className="font-medium text-gray-900 dark:text-white">{student.name}</span>,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      render: (student) => <span className="text-gray-500">{student.email}</span>,
    },
    {
      key: "createdAt",
      label: "Registrado",
      sortable: true,
      render: (student) => (
        <span className="text-xs text-gray-500">
          {new Date(student.createdAt).toLocaleDateString("es-CO")}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: () => (
        <div className="flex justify-end">
          <RowActionsMenu actions={[{ label: "Verificar certificado", icon: <CheckCircleIcon />, onClick: onOpenVerify }]} />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Programas", "Certificados"]}
      title="Certificados"
      subtitle="Verifica certificados emitidos a estudiantes"
      tabs={[
        { label: "Certificados", to: "/instructor/certificates", active: true },
        { label: "Plantillas", to: "/instructor/certificate-templates" },
      ]}
      metrics={[
        { label: "Total estudiantes", value: String(studentsTotal), hint: "Con certificados emitidos" },
        { label: "En esta página", value: String(students.length) },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar estudiante por nombre o email...",
        },
        onRefresh: reload,
        refreshing: loading,        onCreate: onOpenVerify,
        createLabel: "Verificar código",
      }}
    >
      {error && (
        <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400 mb-4">
          {error}
        </p>
      )}

      {isVerifyOpen && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/60 p-5 dark:border-blue-800 dark:bg-blue-900/10">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Verificar certificado</h3>
            <button
              type="button"
              onClick={() => setIsVerifyOpen(false)}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Cerrar
            </button>
          </div>
          <form onSubmit={handleVerify} className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs text-gray-500 mb-1 block">Código del certificado</label>
              <input
                type="text"
                required
                autoFocus
                placeholder="SAT-XXXX-XXXX-XXXX"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={verifying}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {verifying ? "Verificando..." : "Verificar"}
            </button>
          </form>

          {verifyResult && (
            <div className="mt-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
                <p className="font-semibold text-green-800 dark:text-green-300 text-sm">
                  Certificado válido
                </p>
              </div>
              <dl className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Estudiante</dt>
                  <dd className="text-gray-900 dark:text-white font-medium">
                    {verifyResult.student_name}
                  </dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Curso</dt>
                  <dd className="text-gray-900 dark:text-white">{verifyResult.course_title}</dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Emitido</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {new Date(verifyResult.issued_at).toLocaleDateString("es-CO")}
                  </dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Código</dt>
                  <dd className="font-mono text-xs text-gray-700 dark:text-gray-300">
                    {verifyResult.code}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      )}

      <div className="mb-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
        El listado completo de certificados por estudiante estará disponible cuando se implemente{" "}
        <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded text-xs">
          GET /admin/certificates
        </code>
        . Por ahora, usa "Verificar código" para confirmar certificados individuales.
      </div>

      <DataTable
        columns={columns}
        rows={students}
        rowKey={(student) => student.id}
        loading={loading}
        emptyMessage="No se encontraron estudiantes."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={studentsTotal}
        onPageChange={setPage}
      />    </ModuleLayout>
  )
}
